import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Text,
  Chip,
  Divider,
  IconButton,
  ContentCopyIcon,
  ArrowOutwardIcon,
} from '@citibox-panel/ui';
import type { DeliveryDetail } from '@citibox-panel/api-client';
import { deliveryStateChipLabel, deliveryStateChipColor } from '../../../lib/deliveryStates';

export interface DeliveryHeroProps {
  detail: DeliveryDetail;
  // Resolves the legacy lock-maintenance URL for a «buzón» id. Injected by the host: the slice
  // never learns a domain, a path or an environment. Returns null when the host cannot name the
  // market, and the icon then does not render — a link to the wrong market's box is worse than none.
  legacyBoxMaintenanceUrl?: (boxId: string) => string | null;
}

const COPIED_FEEDBACK_MS = 1500;

// The hero: a top bar — status chip (CC-friendly ES label + colour via the shared
// delivery-state module, fail-open to the capitalized raw string)
// beside a "Delivery ID" copy chip (outlined pill action) — split from the heading block (tracking · carrier
// line + "Buzón {matrícula}" title, followed by the box-maintenance link out to the legacy panel)
// by a divider. It pads its own regions so the divider
// runs edge to edge of the unpadded card. All three of status/tracking/carrier are
// rendered as React text (default-escaped) — never dangerouslySetInnerHTML — so a
// script-bearing wire value renders inert. The transportista is an object since CBX-10954, so the
// line reads its `name`; a null carrier (an orphan `courier` row) renders the same em dash the
// nullable string did.
export function DeliveryHero({ detail, legacyBoxMaintenanceUrl }: DeliveryHeroProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Clear a pending "Copiado ✓" revert on unmount so it never fires on a gone component.
  useEffect(() => () => {
    clearTimeout(timer.current);
  }, []);

  const handleCopy = () => {
    void navigator.clipboard.writeText(detail.id);
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setCopied(false);
    }, COPIED_FEEDBACK_MS);
  };

  // No box, no id, no link — the same gate «Ver log buzón» uses, for the same reason: box.id is
  // the only variable in the destination.
  const box = detail.box;
  const maintenanceHref =
    box !== null && legacyBoxMaintenanceUrl ? legacyBoxMaintenanceUrl(box.id) : null;

  return (
    <Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, p: 2 }}
      >
        <Chip label={deliveryStateChipLabel(detail.status)} color={deliveryStateChipColor(detail.status)} />
        {/* TODO: i18n — copy en ES hasta Fase 1. */}
        {/* An outlined clickable chip, per the design: a quiet pill (no uppercase), not a Button. */}
        <Chip
          variant="outlined"
          size="medium"
          icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
          label={copied ? 'Copiado ✓' : 'Delivery ID'}
          onClick={handleCopy}
        />
      </Box>
      <Divider />
      <Stack spacing={0.125} sx={{ p: 2 }}>
        <Text variant="body2" color="secondary">
          {`${detail.tracking ?? '—'} · ${detail.carrier?.name ?? '—'}`}
        </Text>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {/* TODO: i18n — copy en ES hasta Fase 1. */}
          <Text variant="h5" component="h2" bold>
            {`Buzón ${detail.box?.number ?? '—'}`}
          </Text>
          {box !== null && maintenanceHref !== null && (
            <IconButton
              href={maintenanceHref}
              color="primary"
              size="small"
              /* TODO: i18n */
              aria-label={`Mantenimiento del buzón ${box.number} en el panel legacy (se abre en una pestaña nueva)`}
            >
              <ArrowOutwardIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
